<ng-template
  #priceLine
  let-label="label"
  let-value="value"
  let-originalValue="originalValue"
  let-labelContent="labelContent"
  let-bold="bold"
  let-labelClass="labelClass"
  let-borderBottom="borderBottom"
  let-category="category"
>
  <tr
    [attr.data-testid]="'price-breakdown-price-for-' + category"
    class="ps-xxsmall-b"
    [ngClass]="{ 'border-bottom': borderBottom }"
  >
    <th
      scope="row"
      class="align-left ps-xxsmall-b"
      [ngClass]="labelClass || (bold ? 'medium-type ms-xsmall-t' : 'light-type')"
    >
      <ng-container *ngIf="label; else labelContent">{{ label }}</ng-container>
    </th>
    <td
      data-testid="price-breakdown-price"
      class="align-right ps-xxsmall-b"
      [ngClass]="{ 'medium-type ms-xsmall-t': bold }"
    >
      <span
        data-testid="price-breakdown-original-price"
        class="nordic-blue-900-text text-icon-primary strike-through pr-xxsmall-r"
        *ngIf="originalValue"
        >{{ originalValue }}</span
      >
      <span data-testid="price-breakdown-price">{{ value }}</span>
    </td>
  </tr>
</ng-template>

<ng-template #titleLine let-label="label" let-labelContent="labelContent" let-bold="bold">
  <tr class="ps-xxsmall-b">
    <th
      scope="row"
      class="align-left ps-xxsmall-y"
      [ngClass]="{ 'light-type': !bold, 'medium-type ms-xsmall-t': bold }"
    >
      <ng-container *ngIf="label; else labelContent">{{ label }}</ng-container>
    </th>
  </tr>
</ng-template>

<ng-container *ngIf="!showInExpand()">
  <ng-container *ngIf="prices$ | async as prices">
    <span *ngIf="helperText()" class="font-body-1"> {{ helperText() | finLocalization }} </span>
    <div class="font-body-1 ps-large-t content font-body-medium">
      <table class="fill summary-table">
        <caption class="is-vishidden">
          {{
            'purchase.priceBreakdown.title' | finLocalization
          }}
        </caption>
        <thead class="is-vishidden">
          <tr>
            <th class="flex medium-type ps-xxsmall-b" scope="col" colspan="2">
              {{ 'purchase.priceBreakdown.flights' | finLocalization }}
            </th>
          </tr>
        </thead>
        <tbody>
          <ng-container
            *ngIf="showPassengerCount()"
            [ngTemplateOutlet]="priceLine"
            [ngTemplateOutletContext]="{
              label:
                (((passengers$ | async)?.length === 1 ? 'tripCard.passenger' : 'tripCard.passengers')
                | finLocalization: { passengerAmount: (passengers$ | async)?.length || 0 }),
              bold: false,
              category: 'flights',
            }"
          />
          <ng-container
            *ngIf="showBaseFare()"
            [ngTemplateOutlet]="priceLine"
            [ngTemplateOutletContext]="{
              label: 'purchase.review.baseFare' | finLocalization,
              value: (prices?.total?.total.baseFare | finPrice),
              bold: false,
              category: 'base-fare',
            }"
          />
          <ng-container
            *ngIf="showFareDifference()"
            [ngTemplateOutlet]="priceLine"
            [ngTemplateOutletContext]="{
              label: 'MMB.voluntaryChange.breakdown.fare.difference' | finLocalization,
              value: (breakdownTotalValue$ | async | finPrice: { absoluteValue: true }),
              bold: false,
              labelClass: 'regular-type',
              category: 'fare-difference',
            }"
          />
        </tbody>
        <tfoot></tfoot>
      </table>

      <ng-container *ngIf="breakdowns$ | async as breakdowns">
        <div *ngIf="breakdowns.length" class="ps-small flex flex--center">
          <button
            (click)="openSummaryModal($event, prices)"
            class="detailed-breakdown underline nordic-blue-900-text text-icon-primary bold-type"
            data-testid="see-detailed-breakdown-button"
          >
            {{ 'purchase.priceBreakdown.detailedBreakdown' | finLocalization }}
          </button>
        </div>
      </ng-container>

      <div
        *ngIf="prices.total?.total?.penalty as penalty"
        class="flex flex--space-between flex--basis-full"
        data-testid="change-fee"
      >
        <span class="font-body-1 nordic-blue-900-text text-icon-primary font-body-medium">
          {{ 'MMB.voluntaryChange.breakdown.fee' | finLocalization }}
        </span>
        <span class="font-body-1 nordic-blue-900-text text-icon-primary font-body-medium">
          {{ { amount: penalty.amount, currencyCode: penalty.currencyCode } | finPrice: { showNegativeSign: true } }}
        </span>
      </div>
    </div>
  </ng-container>
</ng-container>

<ng-container *ngIf="showInExpand()">
  <fin-expand
    *ngIf="prices$ | async as prices"
    class="flex--basis-full block pr-small-x"
    [key]="'price-breakdown-expand'"
    [id]="'price-breakdown-expand'"
    [initiallyOpen]="true"
    [class.ps-small-b]="(expand.isOpen$ | async) === false"
    #expand
  >
    <div class="trigger flex">
      <div class="flex-1">
        <h3 class="font-heading-4 font-title-medium">
          <fcom-icon
            class="flex flex-1 flex--nogrow flex--self-start icon-large nordic-blue-900-text text-icon-primary"
            [name]="SvgLibraryIcon.CREDIT_OR_DEBIT_CARD_BLOCK"
          />
          <span class="flex-1 flex--self-center ps-xsmall-l medium-type">
            {{ 'purchase.priceBreakdown.title' | finLocalization }}
          </span>
        </h3>
      </div>
      <fcom-icon
        class="flex-1 flex--nogrow rotate flex--self-start icon-medium nordic-blue-900-text text-icon-primary"
        [class.rotate-active]="expand.isOpen$ | async"
        [name]="SvgLibraryIcon.CHEVRON_DOWN"
      />
    </div>
    <div class="font-body-1 ps-xsmall-t content font-body-medium">
      <ng-content select="fcom-notification" />
      <table class="fill summary-table">
        <caption class="is-vishidden">
          {{
            'purchase.priceBreakdown.title' | finLocalization
          }}
        </caption>
        <thead>
          <tr>
            <th class="flex medium-type ps-xxsmall-b" scope="col" colspan="2">
              {{ 'purchase.priceBreakdown.flights' | finLocalization }}
            </th>
          </tr>
        </thead>
        <tbody>
          <ng-container
            [ngTemplateOutlet]="priceLine"
            [ngTemplateOutletContext]="{
              label:
                ((passengers$ | async).length === 1 ? 'tripCard.passenger' : 'tripCard.passengers')
                | finLocalization: { passengerAmount: (passengers$ | async).length },
              value:
                showPoints() && prices?.flight?.total.totalPoints
                  ? ('award.points'
                    | finLocalization
                      : {
                          pointsAmount: +prices?.flight?.total?.totalPoints.amount | finPoints,
                        })
                  : (prices?.flight?.total.baseFare | finPrice),
              bold: false,
              category: 'flights',
            }"
          />
          <ng-template #labelContent>
            <button
              data-testid="price-breakdown-total-tax"
              (click)="openTaxModal($event, prices?.flight?.total.totalTax)"
              class="taxes-link underline nordic-blue-900-text text-icon-primary"
            >
              {{ 'purchase.review.taxTotal' | finLocalization }}
            </button>
          </ng-template>
          <ng-container
            [ngTemplateOutlet]="priceLine"
            [ngTemplateOutletContext]="{
              value:
                prices?.flight?.total.totalTax || {
                  amount: '0',
                  currencyCode: prices?.flight?.total.baseFare.currencyCode,
                } | finPrice,
              labelContent: prices?.flight?.total.totalTax ? labelContent : null,
              label: prices?.flight?.total.totalTax ? null : ('purchase.review.taxTotal' | finLocalization),
              borderBottom: prices.services.totalPerCategory?.length,
              bold: true,
              category: 'tax',
            }"
          />

          <ng-container
            *ngIf="showTravelExtrasTitle$ | async"
            [ngTemplateOutlet]="titleLine"
            [ngTemplateOutletContext]="{
              label: 'purchase.priceBreakdown.travelExtras' | finLocalization,
              bold: true,
            }"
          />
          <ng-container *ngIf="summary$ | async as summary">
            <ng-container *ngFor="let sum of summary">
              <ng-container
                *ngIf="sum.count && !(sum.id === Category.SAF)"
                [ngTemplateOutlet]="priceLine"
                [ngTemplateOutletContext]="{
                  label:
                    sum.key
                    | finLocalization
                      : {
                          seatCount: sum.count,
                          mealCount: sum.count,
                          baggageCount: sum.count,
                          loungeCount: sum.count,
                          travelComfortCount: sum.count,
                          wifiCount: sum.count,
                          coverCount: sum.count,
                          petCount: sum.count,
                          sportCount: sum.count,
                          specialCount: sum.count,
                          firearmCount: sum.count,
                          medicCount: sum.count,
                          specialNeedCount: sum.count,
                          childCount: sum.count,
                          bundleCount: sum.count,
                          amount: sum.count,
                        },
                  value: sum.total | finPrice,
                  originalValue: sum.originalTotal | finPrice,
                  bold: false,
                  category: sum.id,
                }"
              />
            </ng-container>
          </ng-container>
          <ng-container
            *ngIf="showSafTitle$ | async"
            [ngTemplateOutlet]="titleLine"
            [ngTemplateOutletContext]="{
              label: 'purchase.priceBreakdown.safContribution' | finLocalization,
              bold: true,
            }"
          />
          <ng-container *ngIf="summary$ | async as summary">
            <ng-container *ngFor="let sum of summary">
              <ng-container
                *ngIf="sum.count && sum.id === Category.SAF"
                [ngTemplateOutlet]="priceLine"
                [ngTemplateOutletContext]="{
                  label:
                    sum.key
                    | finLocalization
                      : {
                          safCount: sum.count,
                        },
                  value: sum.total | finPrice,
                  originalValue: sum.originalTotal | finPrice,
                  bold: false,
                  category: sum.id,
                }"
              />
            </ng-container>
          </ng-container>
          <tr class="border-top">
            <th scope="row" class="align-left ps-xxsmall-t medium-type">
              {{ 'price.total' | finLocalization }}
            </th>
            <td class="align-right ps-xxsmall-t medium-type">
              <span data-testid="price-breakdown-money-total">{{ totalPrice$ | async | finPrice }}</span>
            </td>
          </tr>
        </tbody>
        <tfoot></tfoot>
      </table>

      <div class="ps-small flex flex--center">
        <button
          (click)="openSummaryModal($event, prices)"
          class="detailed-breakdown underline nordic-blue-900-text text-icon-primary bold-type"
          data-testid="see-detailed-breakdown-button"
        >
          {{ 'purchase.priceBreakdown.detailedBreakdown' | finLocalization }}
        </button>
      </div>
    </div>
  </fin-expand>
</ng-container>

<fcom-modal
  [(open)]="taxModalOpen"
  [title]="'purchase.review.taxTotal' | finLocalization"
  [buttons]="ModalButtons.CLOSE"
  (close)="closeTaxModal()"
>
  <div class="pr-large-x">
    <cms-unstyled-fragment
      *ngIf="'fragments.purchaseTaxesAndRefundRenewed.url' | finLocalization"
      class="block font-body-1 unstyled-fragment-line-height pr-xxsmall-y font-body-medium"
      [contentUrl$]="'fragments.purchaseTaxesAndRefundRenewed.url' | finLocalization | finToObservable"
    />

    <table
      class="fill dictionary-table tax-table font-body-1 font-body-medium"
      *ngIf="totalTaxes$ | async as totalTaxes"
    >
      <caption class="is-vishidden">
        {{
          'purchase.review.taxTotal' | finLocalization
        }}
      </caption>
      <thead>
        <tr>
          <th class="bold-type ms-medium-y" scope="col" colspan="2">
            {{ 'purchase.priceBreakdown.perBooking' | finLocalization }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let tax of totalTaxes" class="flex flex--space-between">
          <th scope="row">
            <ng-container *ngIf="tax.code !== ENVIRONMENTAL_SURCHARGE; else environmentalSurcharge">
              {{ 'TAX_CODES.' + tax.code.slice(0, 2) | finLocalization | finEmpty: tax.code.slice(0, 2) }}
            </ng-container>
            <ng-template #environmentalSurcharge>
              {{ 'TAX_CODES.' + ENVIRONMENTAL_SURCHARGE | finLocalization }}
            </ng-template>
          </th>
          <td>{{ tax | finPrice }}</td>
        </tr>
      </tbody>
      <tfoot>
        <tr class="flex flex--space-between border-top medium-type">
          <th scope="row">{{ 'price.total' | finLocalization }}</th>
          <td>{{ taxModalData?.totalTaxes | finPrice }}</td>
        </tr>
      </tfoot>
    </table>
  </div>
</fcom-modal>

<fin-price-breakdown-per-passenger-modal
  [(open)]="summaryModalOpen"
  [title]="'purchase.priceBreakdown.detailedBreakdownPassenger' | finLocalization"
  [breakdowns$]="breakdowns$"
  [showPoints]="showPoints()"
  (closed)="closeSummaryModal()"
/>
